import { useState, useEffect } from "react";
import { Ava } from "../atoms/Ava.jsx";
import { StatementSurvey } from "./StatementSurvey.jsx";
import { KnowledgeAdmin } from "./KnowledgeAdmin.jsx";
import { updateCandidate, getCacheStats, clearPersonaCache, getPersonaSessions, getSessionMessages, uploadPersonaAvatar, getPersonaCredits, fundPersonaCredits, getCreditBundles, updatePersonaVisibility } from "../services/candidates.js";
import { DIAL_CODES, getStatesForCountry, getLgasForState } from "../utils/phone.js";
import { ROLE_GEO_RULES, validateRoleGeo, MULTI_LGA_ROLES } from "./CreatePersonaStep1.jsx";
import { Chat } from "./Chat.jsx";
import { StressTest } from "./StressTest.jsx";
import { ELECTORAL_POSITIONS } from "@qm/shared/constants";
import { F, L } from "../styles/theme.js";

const TABS = ["Edit Profile", "Personal Statement", "Tone", "Knowledge Base", "Sessions", "Preview Chat", "Cache", "Credits"];

/**
 * CLONE-2: Admin persona detail page — 3 tabs for all AI training content.
 * Props:
 *   persona        – the persona object
 *   onBack         – navigate back to list
 *   onPersonaUpdate – callback(updatedPersona) to refresh state in parent
 */
export function AdminPersonaDetail({ persona, activeTab: externalTab, onTabChange: externalSetTab, onBack, onPersonaUpdate }) {
  // Use external tab control from sidebar if provided, otherwise internal state
  const [internalTab, setInternalTab] = useState("Personal Statement");
  const tab = externalTab || internalTab;
  const setTab = externalSetTab || setInternalTab;

  // ── Edit Profile tab state ──────────────────────────────────────────────────
  const [editForm, setEditForm] = useState({
    name: persona?.name ?? "",
    role: persona?.role ?? "",
    accentColor: persona?.accent_color ?? persona?.accentColor ?? persona?.party_color ?? persona?.partyColor ?? "#2563eb",
    location: persona?.location ?? "",
    bio: persona?.bio ?? "",
    tagline: persona?.tagline ?? "",
    // v1.28.2 — short name for voter-facing CTA.
    shortName: persona?.short_name ?? persona?.shortName ?? "",
    geoCountry: persona?.geo_country ?? persona?.geoCountry ?? "",
    geoState: persona?.geo_state ?? persona?.geoState ?? "",
    geoLga: persona?.geo_lga ?? persona?.geoLga ?? "",
    geoLgas: persona?.geo_lgas ?? [],
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editSaveOk, setEditSaveOk] = useState(false);
  const [editErr, setEditErr] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // ── LAUNCH-1: Visibility toggle state ─────────────────────────────────────
  const [visibilityActive, setVisibilityActive] = useState(persona?.active ?? false);
  const [visibilityBusy, setVisibilityBusy] = useState(false);
  const [visibilityMsg, setVisibilityMsg] = useState(null);

  useEffect(() => {
    setVisibilityActive(persona?.active ?? false);
  }, [persona?.id, persona?.active]);

  const handleVisibilityToggle = async () => {
    if (visibilityBusy) return;
    const newActive = !visibilityActive;
    setVisibilityBusy(true);
    setVisibilityMsg(null);
    try {
      const updated = await updatePersonaVisibility(persona.id, newActive);
      setVisibilityActive(newActive);
      onPersonaUpdate?.({ ...persona, active: newActive, ...updated });
      setVisibilityMsg(newActive ? "Clone is now Public" : "Clone is now Private");
      setTimeout(() => setVisibilityMsg(null), 3000);
    } catch (e) {
      setVisibilityMsg("Failed to update visibility");
    } finally {
      setVisibilityBusy(false);
    }
  };

  useEffect(() => {
    setEditForm({
      name: persona?.name ?? "",
      role: persona?.role ?? "",
      accentColor: persona?.accent_color ?? persona?.accentColor ?? persona?.party_color ?? persona?.partyColor ?? "#2563eb",
      location: persona?.location ?? "",
      bio: persona?.bio ?? "",
      tagline: persona?.tagline ?? "",
    // v1.28.2 — short name for voter-facing CTA.
    shortName: persona?.short_name ?? persona?.shortName ?? "",
      geoCountry: persona?.geo_country ?? persona?.geoCountry ?? "",
      geoState: persona?.geo_state ?? persona?.geoState ?? "",
      geoLga: persona?.geo_lga ?? persona?.geoLga ?? "",
      geoLgas: persona?.geo_lgas ?? [],
    });
  }, [persona?.id]);

  const isStandardRole = ELECTORAL_POSITIONS.includes(editForm.role);
  const isEditMultiLga = MULTI_LGA_ROLES.includes(editForm.role);
  const editRoleGeoErr = isStandardRole ? validateRoleGeo(editForm.role, editForm.geoCountry, editForm.geoState, editForm.geoLga, editForm.geoLgas) : null;
  const editStates = editForm.geoCountry ? getStatesForCountry(editForm.geoCountry) : [];
  const editLgas = (editForm.geoCountry && editForm.geoState) ? getLgasForState(editForm.geoCountry, editForm.geoState) : [];
  const editSelCountry = DIAL_CODES.find(d => d.code === editForm.geoCountry);

  const saveProfile = async () => {
    if (editSaving || editRoleGeoErr) return;
    setEditSaving(true);
    setEditErr(null);
    setEditSaveOk(false);
    try {
      const updated = await updateCandidate(persona.id, {
        name: editForm.name,
        role: editForm.role,
        accentColor: editForm.accentColor,
        location: editForm.location || null,
        bio: editForm.bio,
        tagline: editForm.tagline,
        // v1.28.2 — short name; trim + null-on-empty so the server stores
        // null for blanks and the voter UI falls back to the heuristic.
        shortName: editForm.shortName?.trim() || null,
        geoCountry: editForm.geoCountry || null,
        geoState: editForm.geoState || null,
        geoLga: editForm.geoLga || null,
        geoLgas: editForm.geoLgas ?? [],
      });
      setEditSaveOk(true);
      onPersonaUpdate?.({ ...persona, ...updated });
      setTimeout(() => setEditSaveOk(false), 2500);
    } catch (e) {
      setEditErr(e?.message ?? "Save failed");
    } finally {
      setEditSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setEditErr("Only JPEG, PNG, or WebP images are allowed.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setEditErr("Image must be under 2MB.");
      return;
    }
    setAvatarUploading(true);
    setEditErr(null);
    try {
      const result = await uploadPersonaAvatar(persona.id, file);
      onPersonaUpdate?.({ ...persona, photo_url: result.photoUrl });
    } catch (err) {
      setEditErr(err?.message ?? "Upload failed");
    } finally {
      setAvatarUploading(false);
    }
  };

  // ── Personal Statement tab state ────────────────────────────────────────────
  const [statement, setStatement] = useState(persona?.statement ?? "");
  const [stmtSaving, setStmtSaving] = useState(false);
  const [stmtSaveOk, setStmtSaveOk] = useState(false);
  const [stmtErr, setStmtErr] = useState(null);
  const [stmtUpdatedAt, setStmtUpdatedAt] = useState(
    persona?.updated_at ?? persona?.updatedAt ?? null
  );

  // ── Preview Chat mode toggle (Manual Chat vs Stress Test) ──────────────────
  const [previewMode, setPreviewMode] = useState("chat"); // "chat" | "stress"

  // ── Cache tab state ──────────────────────────────────────────────────────────
  const [cacheStats, setCacheStats] = useState(null);
  const [cacheBusy, setCacheBusy] = useState(false);
  const [cacheMsg, setCacheMsg] = useState(null);
  const [cacheThreshold, setCacheThreshold] = useState(persona.cache_threshold ?? 0.85);
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [cacheTestQ, setCacheTestQ] = useState("");
  const [cacheTestResult, setCacheTestResult] = useState(null);
  const [cacheTestLoading, setCacheTestLoading] = useState(false);

  useEffect(() => {
    if (tab !== "Cache" || !persona?.id) return;
    getCacheStats(persona.id).then(setCacheStats).catch(() => setCacheStats(null));
  }, [tab, persona?.id]);

  const handleClearCache = async () => {
    if (!window.confirm(`Clear all cached responses for ${persona.name}? This cannot be undone.`)) return;
    setCacheBusy(true);
    try {
      await clearPersonaCache(persona.id);
      setCacheStats({ totalEntries: 0, totalHits: 0, hitRate: 0 });
      setCacheMsg("✓ Cache cleared");
      setTimeout(() => setCacheMsg(null), 3000);
    } catch (e) { setCacheMsg("⚠ " + (e?.message ?? "Clear failed")); }
    finally { setCacheBusy(false); }
  };

  // ── CRED-2: Credits tab state ──────────────────────────────────────────────
  const [creditData, setCreditData] = useState(null);
  const [creditBundles, setCreditBundles] = useState([]);
  const [fundAmount, setFundAmount] = useState("");
  const [fundDesc, setFundDesc] = useState("");
  const [fundLoading, setFundLoading] = useState(false);
  const [fundMsg, setFundMsg] = useState(null);

  useEffect(() => {
    if (tab !== "Credits" || !persona?.id) return;
    getPersonaCredits(persona.id).then(setCreditData).catch(() => setCreditData(null));
    getCreditBundles().then(d => setCreditBundles(d.bundles ?? d ?? [])).catch(() => {});
  }, [tab, persona?.id]);

  const handleFundCredits = async () => {
    const amt = parseInt(fundAmount, 10);
    if (!amt || amt <= 0) { setFundMsg("⚠ Enter a valid amount"); return; }
    setFundLoading(true);
    try {
      const result = await fundPersonaCredits(persona.id, amt, fundDesc || `Admin funding: ${amt} credits`);
      setCreditData(prev => prev ? { ...prev, balance: result.balanceAfter ?? (prev.balance + amt) } : prev);
      // Refresh transactions
      getPersonaCredits(persona.id).then(setCreditData).catch(() => {});
      setFundAmount(""); setFundDesc("");
      setFundMsg(`✓ Added ${amt.toLocaleString()} credits`);
      setTimeout(() => setFundMsg(null), 4000);
    } catch (e) { setFundMsg("⚠ " + (e?.message ?? "Funding failed")); }
    finally { setFundLoading(false); }
  };

  const handleApplyBundle = async (bundle) => {
    if (!window.confirm(`Add ${bundle.credits.toLocaleString()} credits (${bundle.name} bundle) to ${persona.name}?`)) return;
    setFundLoading(true);
    try {
      const result = await fundPersonaCredits(persona.id, bundle.credits, `${bundle.name} bundle applied`);
      setCreditData(prev => prev ? { ...prev, balance: result.balanceAfter ?? (prev.balance + bundle.credits) } : prev);
      getPersonaCredits(persona.id).then(setCreditData).catch(() => {});
      setFundMsg(`✓ ${bundle.name} bundle applied — ${bundle.credits.toLocaleString()} credits added`);
      setTimeout(() => setFundMsg(null), 4000);
    } catch (e) { setFundMsg("⚠ " + (e?.message ?? "Bundle apply failed")); }
    finally { setFundLoading(false); }
  };

  // ── OPS-3: Cache threshold and test handlers ──
  const handleSaveThreshold = async () => {
    setThresholdSaving(true);
    try {
      await apiFetch(`/api/admin/personas/${persona.id}/cache-threshold`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ threshold: cacheThreshold }),
      });
      setCacheMsg("✓ Threshold saved");
      setTimeout(() => setCacheMsg(null), 3000);
    } catch (e) { setCacheMsg("⚠ " + (e?.message ?? "Save failed")); }
    finally { setThresholdSaving(false); }
  };
  const handleCacheTest = async () => {
    if (!cacheTestQ.trim()) return;
    setCacheTestLoading(true);
    setCacheTestResult(null);
    try {
      const res = await apiFetch(`/api/admin/personas/${persona.id}/cache-test?q=${encodeURIComponent(cacheTestQ)}`);
      setCacheTestResult(res);
    } catch (e) { setCacheTestResult({ error: e?.message ?? "Test failed" }); }
    finally { setCacheTestLoading(false); }
  };

  // ── Sessions tab state (ADMIN-1 + ADMIN-13) ───────────────────────────────────
  const [sessions, setSessions] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsPage, setSessionsPage] = useState(0);
  const [sessionSentimentFilter, setSessionSentimentFilter] = useState(""); // "" | "positive" | "neutral" | "negative"
  const SESSIONS_LIMIT = 20;
  const [activeSession, setActiveSession] = useState(null); // { sessionId, maskedPhone }
  const [sessionMsgs, setSessionMsgs] = useState([]);
  const [sessionMsgsTotal, setSessionMsgsTotal] = useState(0);
  const [sessionMsgsLoading, setSessionMsgsLoading] = useState(false);

  useEffect(() => {
    if (tab !== "Sessions" || !persona?.id || activeSession) return;
    setSessionsLoading(true);
    getPersonaSessions(persona.id, SESSIONS_LIMIT, sessionsPage * SESSIONS_LIMIT, sessionSentimentFilter)
      .then(d => { setSessions(d.sessions ?? []); setSessionsTotal(d.total ?? 0); })
      .catch(() => {})
      .finally(() => setSessionsLoading(false));
  }, [tab, persona?.id, sessionsPage, sessionSentimentFilter]);

  const openSession = (session) => {
    setActiveSession(session);
    setSessionMsgs([]);
    setSessionMsgsLoading(true);
    getSessionMessages(persona.id, session.sessionId, 50, 0)
      .then(d => { setSessionMsgs(d.messages ?? []); setSessionMsgsTotal(d.total ?? 0); })
      .catch(() => {})
      .finally(() => setSessionMsgsLoading(false));
  };

  const loadOlderMessages = () => {
    if (!activeSession || sessionMsgsLoading) return;
    setSessionMsgsLoading(true);
    getSessionMessages(persona.id, activeSession.sessionId, 50, sessionMsgs.length)
      .then(d => { setSessionMsgs(prev => [...(d.messages ?? []), ...prev]); })
      .catch(() => {})
      .finally(() => setSessionMsgsLoading(false));
  };

  // ── Survey tab state ─────────────────────────────────────────────────────────
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyDone, setSurveyDone] = useState(false);

  // Keep statement in sync if parent persona changes
  useEffect(() => {
    setStatement(persona?.statement ?? "");
    setStmtUpdatedAt(persona?.updated_at ?? persona?.updatedAt ?? null);
  }, [persona?.id]);

  const pc = persona?.accentColor ?? persona?.accent_color ?? persona?.partyColor ?? persona?.party_color ?? "#3b82f6";

  // ── Save statement ──────────────────────────────────────────────────────────
  const saveStatement = async () => {
    if (!statement.trim() || stmtSaving) return;
    setStmtSaving(true);
    setStmtErr(null);
    setStmtSaveOk(false);
    try {
      const updated = await updateCandidate(persona.id, { statement });
      setStmtUpdatedAt(updated.updated_at ?? new Date().toISOString());
      setStmtSaveOk(true);
      onPersonaUpdate?.({ ...persona, statement, updated_at: updated.updated_at });
      setTimeout(() => setStmtSaveOk(false), 2500);
    } catch (e) {
      setStmtErr(e?.message ?? "Save failed");
    } finally {
      setStmtSaving(false);
    }
  };

  // ── Survey complements existing statement (BUG-H6 fix) ─────────────────────
  const handleSurveyComplete = async (surveyStatement) => {
    // Merge: keep existing statement and append survey output with section header
    const merged = statement.trim()
      ? `${statement.trim()}\n\n--- Survey Insights ---\n\n${surveyStatement}`
      : surveyStatement;
    setStatement(merged);
    setShowSurvey(false);
    setSurveyDone(true);
    setTab("Personal Statement");
    // Auto-save merged statement
    try {
      const updated = await updateCandidate(persona.id, { statement: merged });
      setStmtUpdatedAt(updated.updated_at ?? new Date().toISOString());
      onPersonaUpdate?.({ ...persona, statement: merged, updated_at: updated.updated_at });
    } catch { /* non-fatal */ }
  };

  if (!persona) return null;

  // Page title map for each tab
  const TAB_TITLES = {
    "Edit Profile": "Profile",
    "Personal Statement": "Personal Statement",
    "Tone": "Tone & Style",
    "Knowledge Base": "Knowledge Base",
    "Sessions": "Sessions",
    "Cache": "Cache",
    "Credits": "Credits",
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900 }}>
      {/* Page header — consistent with Dashboard/Analytics */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>
          {persona.name}
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif", margin: 0 }}>
          {TAB_TITLES[tab] || tab}
        </h1>
      </div>

      {/* ═══════════ TAB: Edit Profile ═══════════ */}
      {tab === "Edit Profile" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ padding: "9px 12px", background: "#3b82f612", border: "1px solid #3b82f628", borderRadius: 8, fontSize: 12, color: "#60a5fa" }}>
            Slug: <strong style={{ fontFamily: "monospace" }}>{persona.slug}</strong> — cannot be changed after creation.
          </div>

          {/* LAUNCH-1: Clone Visibility toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: visibilityActive ? "#16a34a12" : "#64748b12", border: `1px solid ${visibilityActive ? "#16a34a33" : "#64748b33"}`, borderRadius: 8 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>Clone Visibility</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{visibilityActive ? "Public — visible to citizens" : "Private — hidden from citizens"}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {visibilityMsg && <span style={{ fontSize: 11, color: visibilityActive ? "#22c55e" : "#94a3b8" }}>{visibilityMsg}</span>}
              <button
                data-testid="visibility-toggle"
                onClick={handleVisibilityToggle}
                disabled={visibilityBusy}
                style={{
                  position: "relative", width: 44, height: 24, borderRadius: 12, border: "none", cursor: visibilityBusy ? "not-allowed" : "pointer",
                  background: visibilityActive ? "#16a34a" : "#475569", transition: "background 0.2s",
                  opacity: visibilityBusy ? 0.6 : 1,
                }}
              >
                <span style={{
                  position: "absolute", top: 2, left: visibilityActive ? 22 : 2,
                  width: 20, height: 20, borderRadius: "50%", background: "#fff",
                  transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }} />
              </button>
            </div>
          </div>

          {/* ADMIN-5: Avatar upload */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Ava ini={persona.avatar} color={pc} size={56} photoUrl={persona.photo_url ?? persona.photoUrl} />
            <div>
              <label style={{ ...L, marginBottom: 4 }}>Avatar Photo</label>
              <label style={{
                display: "inline-block", padding: "7px 14px", background: "#1e3a5f",
                border: "1px solid #3b82f640", borderRadius: 8, color: "#60a5fa",
                fontSize: 12, fontWeight: 600, cursor: avatarUploading ? "not-allowed" : "pointer",
                opacity: avatarUploading ? 0.6 : 1,
              }}>
                {avatarUploading ? "Uploading…" : (persona.photo_url || persona.photoUrl) ? "Change Photo" : "Upload Photo"}
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleAvatarUpload}
                  style={{ display: "none" }} disabled={avatarUploading} />
              </label>
              <div style={{ fontSize: 10, color: "#475569", marginTop: 4 }}>JPEG, PNG, or WebP · Max 2MB</div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label style={L}>Full Name *</label>
            <input style={F} value={editForm.name}
              onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Electoral Position */}
            <div>
              <label style={L}>Electoral Position</label>
              <div style={{ position: "relative" }}>
                <select className="qm-sel" value={ELECTORAL_POSITIONS.includes(editForm.role) ? editForm.role : (editForm.role ? "__other__" : "")}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === "__other__") setEditForm(f => ({ ...f, role: "" }));
                    else setEditForm(f => ({ ...f, role: v }));
                  }}>
                  <option value="">Select position...</option>
                  {ELECTORAL_POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  <option value="__other__">Other (custom)</option>
                </select>
                <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", color: "#475569", pointerEvents: "none", fontSize: 11 }}>▾</span>
              </div>
              {!ELECTORAL_POSITIONS.includes(editForm.role) && (
                <input style={{ ...F, marginTop: 8 }} placeholder="e.g. Mayor, Councillor" value={editForm.role}
                  onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} />
              )}
            </div>
            {/* Display Location */}
            <div>
              <label style={L}>Display Location</label>
              <input style={F} value={editForm.location}
                onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} />
            </div>
          </div>

          {/* v1.27.0: Party input removed. Only the accent-colour picker
              remains, reframed from "Party Colour" to "Accent Colour". */}
          <div>
            <label style={L}>Accent Colour</label>
            <div style={{ display: "flex", gap: 7, paddingTop: 5 }}>
              {["#2563eb","#dc2626","#16a34a","#7c3aed","#0891b2","#ea580c"].map(c => (
                <button key={c} onClick={() => setEditForm(f => ({ ...f, accentColor: c }))}
                  style={{ width: 26, height: 26, borderRadius: "50%", background: c, cursor: "pointer", padding: 0,
                    border: editForm.accentColor === c ? "3px solid #fff" : "3px solid transparent",
                    outline: editForm.accentColor === c ? `2px solid ${c}` : "none" }} />
              ))}
            </div>
          </div>

          {/* v1.28.2 — Short name for voter-facing CTA ("Ask NDDC a Question") */}
          <div>
            <label style={L}>
              Short Name <span style={{ color: "#334155", fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>(optional — used in "Ask ___ a Question")</span>
            </label>
            <input
              style={F}
              placeholder={(persona?.persona_type ?? persona?.personaType) === "agency" ? "e.g. NDDC" : "e.g. President Tinubu"}
              value={editForm.shortName ?? ""}
              onChange={e => setEditForm(f => ({ ...f, shortName: e.target.value }))}
            />
          </div>

          {/* Tagline */}
          <div>
            <label style={L}>Tagline</label>
            <input style={F} placeholder="Short public tagline" value={editForm.tagline}
              onChange={e => setEditForm(f => ({ ...f, tagline: e.target.value }))} />
          </div>

          {/* Bio */}
          <div>
            <label style={L}>Profile Bio</label>
            <textarea style={{ ...F, minHeight: 90, resize: "vertical", lineHeight: 1.6 }}
              value={editForm.bio} onChange={e => setEditForm(f => ({ ...f, bio: e.target.value }))} />
          </div>

          {/* Geo Scope */}
          <div style={{ background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "#3b82f6", textTransform: "uppercase", marginBottom: 14 }}>
              Geo Scope
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={L}>Country</label>
                <div style={{ position: "relative" }}>
                  <select className="qm-sel" value={editForm.geoCountry}
                    onChange={e => setEditForm(f => ({ ...f, geoCountry: e.target.value, geoState: "", geoLga: "" }))}>
                    <option value="">All countries</option>
                    {DIAL_CODES.map(d => <option key={d.code} value={d.code}>{d.flag} {d.country}</option>)}
                  </select>
                  <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", color: "#475569", pointerEvents: "none", fontSize: 11 }}>▾</span>
                </div>
              </div>
              {editForm.geoCountry && editStates.length > 0 && (
                <div>
                  <label style={L}>{editSelCountry?.stateLabel || "State"}</label>
                  <div style={{ position: "relative" }}>
                    <select className="qm-sel" value={editForm.geoState}
                      onChange={e => setEditForm(f => ({ ...f, geoState: e.target.value, geoLga: "" }))}>
                      <option value="">All {editSelCountry?.stateLabel || "states"}</option>
                      {editStates.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", color: "#475569", pointerEvents: "none", fontSize: 11 }}>▾</span>
                  </div>
                </div>
              )}
              {editForm.geoState && editLgas.length > 0 && (
                <div>
                  <label style={L}>
                    {editSelCountry?.lgaLabel || "Local Area"}
                    {isEditMultiLga && <span style={{ color: "#3b82f6", fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}> (select multiple)</span>}
                  </label>
                  {isEditMultiLga ? (
                    <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #1e2d45", borderRadius: 9, background: "#0f1623", padding: "8px 10px" }}>
                      {editLgas.map(l => (
                        <label key={l} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px", cursor: "pointer", fontSize: 13, color: "#e2e8f0" }}>
                          <input type="checkbox" checked={(editForm.geoLgas ?? []).includes(l)}
                            onChange={e => {
                              const checked = e.target.checked;
                              setEditForm(f => ({
                                ...f,
                                geoLgas: checked ? [...(f.geoLgas ?? []), l] : (f.geoLgas ?? []).filter(x => x !== l),
                                geoLga: "",
                              }));
                            }}
                            style={{ accentColor: "#3b82f6" }} />
                          {l}
                        </label>
                      ))}
                      {(editForm.geoLgas ?? []).length > 0 && (
                        <div style={{ fontSize: 10, color: "#60a5fa", marginTop: 6 }}>
                          {(editForm.geoLgas ?? []).length} selected
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ position: "relative" }}>
                      <select className="qm-sel" value={editForm.geoLga}
                        onChange={e => setEditForm(f => ({ ...f, geoLga: e.target.value }))}>
                        <option value="">All {editSelCountry?.lgaLabel || "areas"}</option>
                        {editLgas.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", color: "#475569", pointerEvents: "none", fontSize: 11 }}>▾</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {editRoleGeoErr && (
            <div style={{ padding: "9px 12px", background: "#ef444412", border: "1px solid #ef444430", borderRadius: 8, fontSize: 12, color: "#ef4444" }}>
              ⚠ {editRoleGeoErr}
            </div>
          )}
          {editErr && (
            <div style={{ padding: "8px 12px", background: "#ef444414", border: "1px solid #ef444430", borderRadius: 8, fontSize: 12, color: "#ef4444" }}>
              ⚠ {editErr}
            </div>
          )}
          {editSaveOk && (
            <div style={{ padding: "8px 12px", background: "#22c55e14", border: "1px solid #22c55e30", borderRadius: 8, fontSize: 12, color: "#22c55e" }}>
              ✓ Profile saved
            </div>
          )}

          <button onClick={saveProfile} disabled={editSaving || !editForm.name || !!editRoleGeoErr}
            style={{
              padding: "13px",
              background: (editSaving || !editForm.name || editRoleGeoErr) ? "#1e2d45" : `linear-gradient(135deg,${pc},${pc}cc)`,
              color: (editSaving || !editForm.name || editRoleGeoErr) ? "#475569" : "#fff",
              border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700,
              cursor: (editSaving || !editForm.name || editRoleGeoErr) ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}>
            {editSaving ? "Saving..." : "Save Profile"}
          </button>
        </div>
      )}

      {/* ═══════════ TAB: Personal Statement ═══════════ */}
      {tab === "Personal Statement" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {surveyDone && (
            <div style={{ padding: "9px 13px", background: "#22c55e14", border: "1px solid #22c55e30", borderRadius: 8, fontSize: 12, color: "#22c55e" }}>
              ✓ Statement updated from tone builder and saved
            </div>
          )}

          <div>
            <label style={L}>AI Personal Statement</label>
            <textarea
              style={{ ...F, minHeight: 220, resize: "vertical", lineHeight: 1.65 }}
              value={statement}
              onChange={e => { setStatement(e.target.value); setStmtSaveOk(false); setSurveyDone(false); }}
              placeholder="This statement trains the AI clone — describe values, policy positions, communication style, background..."
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 5 }}>
              <span style={{ fontSize: 11, color: "#475569" }}>{statement.length} characters</span>
              {stmtUpdatedAt && (
                <span style={{ fontSize: 10, color: "#334155" }}>
                  Last saved: {new Date(stmtUpdatedAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>

          {stmtErr && (
            <div style={{ padding: "8px 12px", background: "#ef444414", border: "1px solid #ef444430", borderRadius: 8, fontSize: 12, color: "#ef4444" }}>
              ⚠ {stmtErr}
            </div>
          )}
          {stmtSaveOk && (
            <div style={{ padding: "8px 12px", background: "#22c55e14", border: "1px solid #22c55e30", borderRadius: 8, fontSize: 12, color: "#22c55e" }}>
              ✓ Statement saved
            </div>
          )}

          <button onClick={saveStatement} disabled={stmtSaving || !statement.trim()}
            style={{
              padding: "13px",
              background: stmtSaving || !statement.trim() ? "#1e2d45" : `linear-gradient(135deg,${pc},${pc}cc)`,
              color: stmtSaving || !statement.trim() ? "#475569" : "#fff",
              border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700,
              cursor: stmtSaving || !statement.trim() ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}>
            {stmtSaving ? "⟳ Saving…" : "💾 Save Statement"}
          </button>

          <div style={{ padding: "1px 0" }}>
            <button onClick={() => setTab("Tone")}
              style={{ background: "none", border: "none", color: "#3b82f6", fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
              ↻ Regenerate via Tone Builder →
            </button>
          </div>
        </div>
      )}

      {/* ═══════════ TAB: Survey ═══════════ */}
      {tab === "Tone" && (
        <div>
          {!showSurvey ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 36 }}>🧠</div>
              <div style={{ textAlign: "center", maxWidth: 400 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", marginBottom: 8 }}>
                  Regenerate Statement from Tone Builder
                </div>
                <p style={{ color: "#64748b", fontSize: 13, lineHeight: 1.6, margin: "0 0 20px" }}>
                  Answer a series of questions about {persona.name}'s communication style, values, and tone.
                  The tone builder will compile a new AI training statement and automatically save it.
                </p>
                <div style={{ padding: "10px 14px", background: "#3b82f612", border: "1px solid #3b82f628", borderRadius: 8, fontSize: 12, color: "#60a5fa", marginBottom: 20, textAlign: "left" }}>
                  ℹ Tone insights will be <strong>added to</strong> the existing personal statement, not replace it.
                </div>
                <button onClick={() => setShowSurvey(true)}
                  style={{
                    padding: "12px 28px", borderRadius: 10, border: "none",
                    background: `linear-gradient(135deg,${pc},${pc}cc)`,
                    color: "#fff", fontSize: 14, fontWeight: 700,
                    cursor: "pointer", fontFamily: "inherit",
                  }}>
                  🧠 Start Tone Builder
                </button>
              </div>
            </div>
          ) : (
            <StatementSurvey
              form={{ name: persona.name, role: persona.role, geoCountry: persona.geo_country }}
              onComplete={handleSurveyComplete}
              onCancel={() => setShowSurvey(false)}
            />
          )}
        </div>
      )}

      {/* ═══════════ TAB: Knowledge Base ═══════════ */}
      {tab === "Knowledge Base" && (
        <KnowledgeAdmin
          persona={persona}
          onBack={() => setTab("Personal Statement")}
          hideBackButton
        />
      )}

      {/* ═══════════ TAB: Sessions (ADMIN-1) ═══════════ */}
      {tab === "Sessions" && (
        <div>
          {activeSession ? (
            /* ── Session chat viewer ─────────────────────────────── */
            <div>
              <button onClick={() => { setActiveSession(null); setSessionMsgs([]); }}
                style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 13, marginBottom: 16, padding: 0, fontFamily: "inherit" }}>
                ← Back to sessions
              </button>
              <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 9, background: "#0d1724", border: "1px solid #1e2d45", fontSize: 12, color: "#94a3b8" }}>
                Session: <strong style={{ color: "#e2e8f0", fontFamily: "monospace" }}>{activeSession.maskedPhone}</strong>
                <span style={{ marginLeft: 12, color: "#64748b" }}>{sessionMsgsTotal} messages</span>
              </div>

              {/* Load older */}
              {sessionMsgs.length < sessionMsgsTotal && (
                <div style={{ textAlign: "center", marginBottom: 12 }}>
                  <button onClick={loadOlderMessages} disabled={sessionMsgsLoading}
                    style={{ padding: "7px 18px", background: "#1e3a5f", border: "1px solid #3b82f640", borderRadius: 8, color: "#60a5fa", fontSize: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                    {sessionMsgsLoading ? "Loading…" : "↑ Load previous messages"}
                  </button>
                </div>
              )}

              {/* Messages */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 500, overflowY: "auto", padding: "4px 0" }}>
                {sessionMsgsLoading && sessionMsgs.length === 0 && (
                  <div style={{ textAlign: "center", color: "#64748b", padding: 20 }}>Loading…</div>
                )}
                {sessionMsgs.map((m, i) => (
                  <div key={m.id ?? i} style={{
                    alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "80%",
                    padding: "10px 14px",
                    borderRadius: 12,
                    background: m.role === "user" ? "#1e3a5f" : "#0d1724",
                    border: `1px solid ${m.role === "user" ? "#3b82f640" : "#1e2d45"}`,
                    color: "#e2e8f0",
                    fontSize: 13,
                    lineHeight: 1.6,
                  }}>
                    <div style={{ fontSize: 10, color: "#64748b", marginBottom: 4 }}>
                      {m.role === "user" ? "Citizen" : persona.name}
                      {m.feedback && <span style={{ marginLeft: 8, color: m.feedback === "liked" ? "#22c55e" : m.feedback === "flagged" ? "#ef4444" : "#f59e0b" }}>
                        {m.feedback === "liked" ? "👍" : m.feedback === "disliked" ? "👎" : "🚩"} {m.feedback}
                      </span>}
                    </div>
                    {m.content}
                    <div style={{ fontSize: 9, color: "#475569", marginTop: 4 }}>
                      {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* ── Session list ────────────────────────────────────── */
            <div>
              <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 9, background: "#0d1724", border: "1px solid #1e2d45", fontSize: 12, color: "#94a3b8" }}>
                {sessionsTotal} citizen session{sessionsTotal !== 1 ? "s" : ""} with {persona.name}
              </div>

              {/* ADMIN-13: Sentiment filter pills — colour-coded per sentiment */}
              <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
                {[["", "All", "#3b82f6"], ["positive", "Positive", "#22c55e"], ["neutral", "Neutral", "#f59e0b"], ["negative", "Negative", "#ef4444"]].map(([key, label, c]) => (
                  <button key={key} onClick={() => { setSessionSentimentFilter(key); setSessionsPage(0); }}
                    style={{ padding: "6px 14px", borderRadius: 7, border: sessionSentimentFilter === key ? `1px solid ${c}66` : "1px solid #1e2d45", background: sessionSentimentFilter === key ? `${c}22` : "transparent", color: sessionSentimentFilter === key ? c : "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    {label}
                  </button>
                ))}
              </div>

              {sessionsLoading ? (
                <div style={{ textAlign: "center", color: "#64748b", padding: 20 }}>Loading sessions…</div>
              ) : sessions.length === 0 ? (
                <div style={{ textAlign: "center", color: "#475569", padding: "40px 0", fontSize: 14 }}>
                  No sessions yet. Voters will appear here once they chat with this persona.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {sessions.map(s => {
                    const sentColor = s.dominantSentiment === "positive" ? "#22c55e" : s.dominantSentiment === "negative" ? "#ef4444" : s.dominantSentiment === "neutral" ? "#f59e0b" : "#475569";
                    const sentLabel = s.dominantSentiment === "unknown" ? "No data" : s.dominantSentiment;
                    const bp = s.sentimentBreakdown ?? { positive: 0, neutral: 0, negative: 0 };
                    const bpTotal = bp.positive + bp.neutral + bp.negative;
                    return (
                    <button key={s.sessionId} onClick={() => openSession(s)}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        width: "100%", padding: "12px 14px", background: "#0f1623",
                        border: `1px solid ${sentColor}25`, borderRadius: 10, cursor: "pointer",
                        fontFamily: "inherit", textAlign: "left", borderLeft: `3px solid ${sentColor}`,
                      }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                        {/* Sentiment indicator dot */}
                        <span style={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, background: sentColor }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ color: "#e2e8f0", fontSize: 14, fontFamily: "monospace", fontWeight: 600 }}>{s.maskedPhone}</span>
                            <span style={{ fontSize: 10, color: sentColor, fontWeight: 600, textTransform: "capitalize" }}>{sentLabel}</span>
                          </div>
                          <div style={{ color: "#64748b", fontSize: 11, marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
                            <span>{s.messageCount} message{s.messageCount !== 1 ? "s" : ""} · {new Date(s.lastActive).toLocaleDateString()}</span>
                            {bpTotal > 0 && (
                              <span style={{ display: "inline-flex", gap: 4, fontSize: 10, color: "#475569" }}>
                                {bp.positive > 0 && <span style={{ color: "#22c55e" }}>+{bp.positive}</span>}
                                {bp.neutral > 0 && <span style={{ color: "#f59e0b" }}>~{bp.neutral}</span>}
                                {bp.negative > 0 && <span style={{ color: "#ef4444" }}>-{bp.negative}</span>}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <span style={{ color: "#475569", fontSize: 16 }}>→</span>
                    </button>
                    );
                  })}

                  {/* Pagination */}
                  {sessionsTotal > SESSIONS_LIMIT && (
                    <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 10 }}>
                      <button onClick={() => setSessionsPage(p => Math.max(0, p - 1))} disabled={sessionsPage === 0}
                        style={{ padding: "6px 14px", background: "#1e3a5f", border: "1px solid #3b82f640", borderRadius: 7, color: sessionsPage === 0 ? "#475569" : "#60a5fa", fontSize: 12, cursor: sessionsPage === 0 ? "default" : "pointer", fontFamily: "inherit" }}>
                        ← Prev
                      </button>
                      <span style={{ color: "#64748b", fontSize: 12, padding: "6px 0" }}>
                        {sessionsPage + 1} / {Math.ceil(sessionsTotal / SESSIONS_LIMIT)}
                      </span>
                      <button onClick={() => setSessionsPage(p => p + 1)} disabled={(sessionsPage + 1) * SESSIONS_LIMIT >= sessionsTotal}
                        style={{ padding: "6px 14px", background: "#1e3a5f", border: "1px solid #3b82f640", borderRadius: 7, color: (sessionsPage + 1) * SESSIONS_LIMIT >= sessionsTotal ? "#475569" : "#60a5fa", fontSize: 12, cursor: (sessionsPage + 1) * SESSIONS_LIMIT >= sessionsTotal ? "default" : "pointer", fontFamily: "inherit" }}>
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══════════ TAB: Preview Chat ═══════════ */}
      {tab === "Preview Chat" && (
        <div>
          {/* Mode toggle: Manual Chat vs Stress Test */}
          <div style={{ display: "flex", gap: 0, marginBottom: 16, borderRadius: 9, overflow: "hidden", border: "1px solid #1e2d45" }}>
            <button
              onClick={() => setPreviewMode("chat")}
              style={{
                flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer",
                border: "none", transition: "all 0.2s",
                background: previewMode === "chat" ? "#2563eb" : "#0f1623",
                color: previewMode === "chat" ? "#fff" : "#64748b",
              }}
            >
              Manual Chat
            </button>
            <button
              onClick={() => setPreviewMode("stress")}
              style={{
                flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer",
                border: "none", borderLeft: "1px solid #1e2d45", transition: "all 0.2s",
                background: previewMode === "stress" ? "#dc2626" : "#0f1623",
                color: previewMode === "stress" ? "#fff" : "#64748b",
              }}
            >
              Stress Test
            </button>
          </div>

          {previewMode === "chat" ? (
            <>
              <div style={{
                padding: "10px 14px", marginBottom: 16, borderRadius: 9,
                background: "#eab30812", border: "1px solid #eab30830",
                fontSize: 12, color: "#eab308", lineHeight: 1.6,
              }}>
                Admin Preview — messages are not counted in analytics. Rate limiting is skipped.
              </div>
              <div style={{ border: "1px solid #1e2d45", borderRadius: 12, overflow: "hidden", minHeight: 400 }}>
                <Chat
                  persona={persona}
                  onBack={() => setTab("Edit Profile")}
                  isAdmin={true}
                  voterPhone={`admin-preview-${persona.id}`}
                  xpState={null}
                  onFeedback={() => {}}
                  onUpdateConv={() => {}}
                  onXpEarned={() => {}}
                />
              </div>
            </>
          ) : (
            <StressTest persona={persona} />
          )}
        </div>
      )}

      {/* ═══════════ TAB: Cache ═══════════ */}
      {tab === "Cache" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ padding: "10px 14px", background: "#0f1623", border: "1px solid #1e2d45", borderRadius: 9, fontSize: 12, color: "#64748b" }}>
            Responses with a quality score ≥ 2 are cached for 30 days. Cached answers return instantly without a Claude call.
          </div>
          {!cacheStats ? (
            <div style={{ color: "#64748b", fontSize: 13 }}>Loading stats…</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              {[["Cached Entries", cacheStats.totalEntries], ["Total Hits", cacheStats.totalHits], ["Hit Rate", `${cacheStats.hitRate}%`]].map(([label, val]) => (
                <div key={label} style={{ padding: "14px", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 10, textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: "#60a5fa" }}>{val}</div>
                  <div style={{ fontSize: 10, color: "#64748b", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
                </div>
              ))}
            </div>
          )}
          {cacheMsg && (
            <div style={{ padding: "8px 12px", background: cacheMsg.startsWith("✓") ? "#22c55e14" : "#ef444414", border: `1px solid ${cacheMsg.startsWith("✓") ? "#22c55e30" : "#ef444430"}`, borderRadius: 8, fontSize: 12, color: cacheMsg.startsWith("✓") ? "#22c55e" : "#ef4444" }}>
              {cacheMsg}
            </div>
          )}
          {/* OPS-3: Cache Threshold Tuning */}
          <div style={{ padding: "14px", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Similarity Threshold</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input type="range" min="0.50" max="0.99" step="0.01" value={cacheThreshold}
                onChange={e => setCacheThreshold(parseFloat(e.target.value))}
                style={{ flex: 1, accentColor: "#3b82f6" }} />
              <span style={{ fontSize: 15, fontWeight: 700, color: "#60a5fa", minWidth: 40, textAlign: "right" }}>{cacheThreshold.toFixed(2)}</span>
              <button onClick={handleSaveThreshold} disabled={thresholdSaving}
                style={{ padding: "6px 14px", background: "#1e3a5f", color: "#60a5fa", border: "1px solid #3b82f630", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                {thresholdSaving ? "…" : "Save"}
              </button>
            </div>
            <div style={{ fontSize: 10, color: "#475569", marginTop: 6 }}>
              Lower = more cache hits (faster, cheaper) · Higher = stricter matching (more accurate)
            </div>
          </div>

          {/* OPS-3: Test Question */}
          <div style={{ padding: "14px", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Test Cache Match</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input type="text" placeholder="Type a question to test cache matching…" value={cacheTestQ}
                onChange={e => setCacheTestQ(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleCacheTest()}
                style={{ flex: 1, padding: "8px 12px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 7, color: "#f1f5f9", fontSize: 12, fontFamily: "inherit" }} />
              <button onClick={handleCacheTest} disabled={cacheTestLoading || !cacheTestQ.trim()}
                style={{ padding: "8px 16px", background: "#1e3a5f", color: "#60a5fa", border: "1px solid #3b82f630", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                {cacheTestLoading ? "…" : "Test"}
              </button>
            </div>
            {cacheTestResult && !cacheTestResult.error && (
              <div style={{ marginTop: 10, fontSize: 12 }}>
                {cacheTestResult.results?.map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: i < cacheTestResult.results.length - 1 ? "1px solid #1e2d45" : "none" }}>
                    <span style={{ fontSize: 14 }}>{r.hit ? "✅" : "❌"}</span>
                    <span style={{ color: "#64748b", fontSize: 11, minWidth: 70 }}>@ {r.threshold.toFixed(2)}</span>
                    {r.hit && <span style={{ color: "#94a3b8", fontSize: 11, flex: 1 }} title={r.matchedQuestion}>{r.matchedQuestion?.slice(0, 60)}…</span>}
                    {r.similarity && <span style={{ color: "#60a5fa", fontSize: 11, fontWeight: 700 }}>{(r.similarity * 100).toFixed(1)}%</span>}
                  </div>
                ))}
              </div>
            )}
            {cacheTestResult?.error && (
              <div style={{ marginTop: 8, color: "#ef4444", fontSize: 11 }}>{cacheTestResult.error}</div>
            )}
          </div>

          <button onClick={handleClearCache} disabled={cacheBusy || !cacheStats || cacheStats.totalEntries === 0}
            style={{ padding: "12px 18px", background: cacheBusy || !cacheStats?.totalEntries ? "#1e2d45" : "#7f1d1d", color: cacheBusy || !cacheStats?.totalEntries ? "#475569" : "#fca5a5", border: "1px solid #991b1b", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {cacheBusy ? "⟳ Clearing…" : "🗑 Clear All Cached Responses"}
          </button>
        </div>
      )}

      {/* ── CRED-2: Credits Tab ─────────────────────────────────────────── */}
      {tab === "Credits" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Balance card */}
          <div style={{ padding: "20px", background: "linear-gradient(135deg, #1e1b4b, #0f1623)", border: "1px solid #3b82f640", borderRadius: 12, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#94a3b8", marginBottom: 8 }}>Credit Balance</div>
            <div style={{ fontSize: 42, fontWeight: 900, color: creditData?.balance > 0 ? "#60a5fa" : "#ef4444" }}>
              {creditData ? creditData.balance.toLocaleString() : "—"}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
              1 credit = 1 chat interaction
            </div>
            {creditData?.balance !== undefined && creditData.balance < 100 && creditData.balance > 0 && (
              <div style={{ marginTop: 10, padding: "6px 12px", background: "#7c2d1230", border: "1px solid #f59e0b30", borderRadius: 8, fontSize: 11, color: "#f59e0b", display: "inline-block" }}>
                ⚠ Low balance — fund to continue conversations
              </div>
            )}
            {creditData?.balance === 0 && (
              <div style={{ marginTop: 10, padding: "6px 12px", background: "#7f1d1d30", border: "1px solid #ef444430", borderRadius: 8, fontSize: 11, color: "#ef4444", display: "inline-block" }}>
                ⛔ No credits — citizens cannot chat until funded
              </div>
            )}
          </div>

          {fundMsg && (
            <div style={{ padding: "8px 12px", background: fundMsg.startsWith("✓") ? "#22c55e14" : "#ef444414", border: `1px solid ${fundMsg.startsWith("✓") ? "#22c55e30" : "#ef444430"}`, borderRadius: 8, fontSize: 12, color: fundMsg.startsWith("✓") ? "#22c55e" : "#ef4444" }}>
              {fundMsg}
            </div>
          )}

          {/* Quick fund bundles */}
          {creditBundles.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748b", marginBottom: 10 }}>Apply Bundle</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
                {creditBundles.map(b => (
                  <button key={b.id} onClick={() => handleApplyBundle(b)} disabled={fundLoading}
                    style={{ padding: "14px 12px", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: "#e2e8f0" }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{b.name}</div>
                    <div style={{ fontSize: 20, fontWeight: 900, color: "#60a5fa", margin: "4px 0" }}>{b.credits.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>₦{((b.priceNaira ?? b.price_naira ?? 0) / 100).toLocaleString()} · ₦{b.pricePerInteraction ?? b.price_per_interaction}/chat</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Manual fund */}
          <div style={{ padding: "14px", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Direct Fund</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input type="number" placeholder="Amount" value={fundAmount} onChange={e => setFundAmount(e.target.value)}
                style={{ flex: "0 0 100px", padding: "8px 12px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 7, color: "#f1f5f9", fontSize: 13, fontFamily: "inherit" }} />
              <input type="text" placeholder="Reason (optional)" value={fundDesc} onChange={e => setFundDesc(e.target.value)}
                style={{ flex: 1, padding: "8px 12px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 7, color: "#f1f5f9", fontSize: 13, fontFamily: "inherit" }} />
              <button onClick={handleFundCredits} disabled={fundLoading || !fundAmount}
                style={{ padding: "8px 18px", background: "#1e3a5f", color: "#60a5fa", border: "1px solid #3b82f630", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                {fundLoading ? "…" : "Fund"}
              </button>
            </div>
          </div>

          {/* Transaction history */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748b", marginBottom: 10 }}>Recent Transactions</div>
            {!creditData?.transactions?.length ? (
              <div style={{ color: "#475569", fontSize: 13, fontStyle: "italic" }}>No transactions yet</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {creditData.transactions.map((t, i) => (
                  <div key={t.id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: i % 2 === 0 ? "#0f1623" : "transparent", borderRadius: 6, fontSize: 12 }}>
                    <span style={{ fontSize: 14 }}>{t.type === "usage" ? "📤" : t.type === "admin_fund" ? "💰" : t.type === "purchase" ? "💳" : "↩️"}</span>
                    <span style={{ flex: 1, color: "#94a3b8" }}>{t.description || t.type}</span>
                    <span style={{ fontWeight: 700, color: t.amount > 0 ? "#22c55e" : "#ef4444", minWidth: 60, textAlign: "right" }}>
                      {t.amount > 0 ? "+" : ""}{t.amount.toLocaleString()}
                    </span>
                    <span style={{ color: "#475569", fontSize: 10, minWidth: 60, textAlign: "right" }}>{(t.balanceAfter ?? t.balance_after)?.toLocaleString()}</span>
                    <span style={{ color: "#334155", fontSize: 10, minWidth: 75 }}>{new Date(t.createdAt ?? t.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPersonaDetail;
